import {VALIDATORS} from '@github-ui/entity-validators'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {CommandButton, ScopedCommands} from '@github-ui/ui-commands'
import type React from 'react'
import {useCallback, useState} from 'react'

import {ERRORS} from '../../constants/errors'
import {useUpdateIssueTitle} from '../../mutations/use-update-issue-title'
import {IssueTitleInput} from '../IssueTitleInput'
import styles from './HeaderEditor.module.css'

type Props = {
  title: string
  titleRef: React.RefObject<HTMLInputElement | null>
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onIssueUpdate?: () => void
  cancelIssueTitleEdit: () => void
  issueId: string
  issueNumber: number
  nameWithOwner: string
  isDirty: boolean
}

export function HeaderEditor({
  onTitleChange,
  title,
  cancelIssueTitleEdit,
  issueId,
  issueNumber,
  nameWithOwner,
  isDirty,
  onIssueUpdate,
  ...props
}: Props) {
  const {addToast} = useToastContext()
  const [validationError, setValidationError] = useState<string | undefined>(undefined)

  const onTitleUpdateError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'error',
      message: ERRORS.couldNotUpdateIssueTitle,
    })
  }, [addToast])

  const updateTitleNatively = useUpdateIssueTitle({
    issueId,
    issueNumber,
    nameWithOwner,
    onError: onTitleUpdateError,
    onCompleted: onIssueUpdate,
  })

  const commitIssueTitleEdit = useCallback(() => {
    updateTitleNatively(title)
  }, [title, updateTitleNatively])

  const isEmpty = title.match(/^ *$/) !== null

  const handleCommitEdit = () => {
    if (isEmpty) {
      setValidationError(VALIDATORS.titleCanNotBeEmpty)
      props.titleRef.current?.focus()
      return
    }
    commitIssueTitleEdit()
    cancelIssueTitleEdit()
  }

  return (
    <div className={styles.HeaderEditorContainer}>
      <ScopedCommands
        commands={{
          'issue-viewer:edit-title-submit': handleCommitEdit,
          'github:submit-form': handleCommitEdit,
        }}
      >
        <IssueTitleInput
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (validationError) {
              setValidationError(undefined)
              props.titleRef.current?.focus()
            }
            onTitleChange(e)
          }}
          cancelIssueTitleEdit={cancelIssueTitleEdit}
          commitIssueTitleEdit={commitIssueTitleEdit}
          isDirty={isDirty}
          validationError={validationError}
          {...props}
        />
        <CommandButton commandId="issue-viewer:close-edit-title" />
        <CommandButton variant="primary" commandId="issue-viewer:edit-title-submit" showKeybindingHint />
      </ScopedCommands>
    </div>
  )
}
